import React, { useEffect, useState } from "react"
import { useSiteContext } from "../hooks/use-site-context"
import { GatsbyImage, StaticImage } from "gatsby-plugin-image"
import { graphql, useStaticQuery } from "gatsby"
import { FaHeart } from "react-icons/fa"
import { MdPlayArrow, MdPause, MdVolumeOff, MdVolumeUp } from "react-icons/md"

const AstrologyReading = () => {
  const imageData = useStaticQuery(graphql`
    {
      allFile(
        filter: { relativeDirectory: { eq: "signs-large" } }
        sort: { name: ASC }
      ) {
        edges {
          node {
            childImageSharp {
              gatsbyImageData(placeholder: BLURRED, quality: 95)
            }
            name
          }
        }
      }
    }
  `)

  const {
    setFirstName,
    mainAudio,
    setCurrentAudioTime,
    setCurrentAudioStage,
    setCurrentStep,
    setAudioEnded,
    selectedSign,
    firstName,
    audioEnded,
    currentAudioStage,
    currentAudioTime,
  } = useSiteContext()

  const [disappearing, setDisappearing] = useState(false)
  const [isTransitioning, setIsTransitioning] = useState(false)
  const [stage, setStage] = useState(1)

  useEffect(() => {
    if (audioEnded) {
      // Go to next clip
      const filename = mainAudio.src.split("/").pop()
      mainAudio.pause()

      if (currentAudioStage === "lets-begin") {
        setCurrentAudioStage(`stars-reveal`)
        mainAudio.src = `/audio/stars-reveal.mp3`
        mainAudio.play()
        setAudioEnded(false)
      } else if (currentAudioStage === "stars-reveal") {
        setCurrentAudioStage(`shrewd-and-perceptive`)
        mainAudio.src = `/audio/shrewd-and-perceptive.mp3`
        mainAudio.play()
        setAudioEnded(false)
        setIsTransitioning(true)
        setTimeout(() => {
          setStage(2)
          setIsTransitioning(false)
        }, 700)
      } else if (currentAudioStage === "shrewd-and-perceptive") {
        setCurrentAudioStage(`heart-good`)
        mainAudio.src = `/audio/heart-good.mp3`
        mainAudio.play()
        setAudioEnded(false)
        setIsTransitioning(true)
        setTimeout(() => {
          setStage(3)
          setIsTransitioning(false)
        }, 700)
      } else if (currentAudioStage === "heart-good") {
        setCurrentAudioStage(`by-the-way`)
        mainAudio.src = `/audio/by-the-way.mp3`
        mainAudio.play()
        setAudioEnded(false)
        setIsTransitioning(true)
        setTimeout(() => {
          setStage(1)
          setIsTransitioning(false)
        }, 700)
      } else {
        setDisappearing(true)
        setTimeout(() => {
          setCurrentAudioTime(0)
          setCurrentAudioStage("enter-email-address")
          mainAudio.src = `/audio/enter-email-address.mp3`
          mainAudio.play()
          setAudioEnded(false)
          setCurrentStep(6)
        }, 700)
      }
    }
  }, [audioEnded])

  return (
    <>
      <div
        className={`text-center fade-in ${
          disappearing ? "disappear transition duration-1000" : ""
        }`}
      >
        <h1 className="text-2xl font-bold text-white md:text-4xl">
          Let's get to know you,
          <br />
          {firstName}{" "}
          <FaHeart className="inline-block -translate-y-0.5 text-brand-pink" />
        </h1>
        {stage === 1 && (
          <div
            className={`relative mt-6 fade-in flex justify-center items-center ${
              isTransitioning ? "disappear transition duration-1000" : ""
            }`}
          >
            <div className="relative">
              {/* Zodiac Glow Image (background) */}
              <GatsbyImage
                image={
                  imageData.allFile.edges.filter(edge =>
                    edge.node.name.includes(selectedSign.toLowerCase())
                  )[0].node.childImageSharp.gatsbyImageData
                }
                loading={"eager"}
                alt={selectedSign}
                className="w-64 h-64 md:w-96 md:h-96 zodiac-glow"
                style={{
                  position: "absolute", // Ensure it layers behind
                  zIndex: 1,
                }}
              />

              {/* Slow Pulse Image (foreground) */}
              <GatsbyImage
                image={
                  imageData.allFile.edges.filter(edge =>
                    edge.node.name.includes(selectedSign.toLowerCase())
                  )[0].node.childImageSharp.gatsbyImageData
                }
                loading={"eager"}
                alt={selectedSign}
                className="w-64 h-64 md:w-96 md:h-96 slow-pulse"
                style={{
                  position: "relative", // Stacks on top
                  zIndex: 2,
                }}
              />
            </div>
          </div>
        )}

        {stage === 2 && (
          <div
            className={`relative mt-6 fade-in flex justify-center items-center ${
              isTransitioning ? "disappear transition duration-1000" : ""
            }`}
          >
            <div className="relative">
              {/* Zodiac Glow Image (background) */}
              <StaticImage
                quality={95}
                loading={"eager"}
                src="../images/jupiter.png"
                placeholder="blurred"
                alt="jupiter in conjunction with neptune"
                className="w-64 h-64 md:w-96 md:h-96 zodiac-glow"
                style={{
                  position: "absolute", // Ensure it layers behind
                  zIndex: 1,
                }}
              />

              {/* Slow Pulse Image (foreground) */}
              <StaticImage
                quality={95}
                loading={"eager"}
                src="../images/jupiter.png"
                placeholder="blurred"
                alt="jupiter in conjunction with neptune"
                className="w-64 h-64 md:w-96 md:h-96 slow-pulse"
                style={{
                  position: "relative", // Stacks on top
                  zIndex: 2,
                }}
              />
            </div>
          </div>
        )}

        {stage === 3 && (
          <div
            className={`relative mt-6 fade-in flex justify-center items-center ${
              isTransitioning ? "disappear transition duration-1000" : ""
            }`}
          >
            <div className="relative">
              {/* Zodiac Glow Image (background) */}
              <StaticImage
                quality={95}
                loading={"eager"}
                src="../images/heart-good.png"
                placeholder="blurred"
                alt="your heart is good"
                className="w-64 h-64 md:w-96 md:h-96 zodiac-glow"
                style={{
                  position: "absolute", // Ensure it layers behind
                  zIndex: 1,
                }}
              />

              {/* Slow Pulse Image (foreground) */}
              <StaticImage
                quality={95}
                loading={"eager"}
                src="../images/heart-good.png"
                placeholder="blurred"
                alt="your heart is good"
                className="w-64 h-64 md:w-96 md:h-96 slow-pulse"
                style={{
                  position: "relative", // Stacks on top
                  zIndex: 2,
                }}
              />
            </div>
          </div>
        )}
      </div>
    </>
  )
}

export default AstrologyReading
